import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import * as SeparatorPrimitive from '@radix-ui/react-separator';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Check, X } from 'lucide-react';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { cn } from "../lib/cn.js";
import { OverlayHostContext, ownsWithin, useOverlayHost, } from "../lib/overlay-host.js";
import { startPointerDrag } from "../lib/pointer-drag.js";
/**
 * Overlay primitives built on Radix.
 *
 * Radix supplies focus trapping, escape handling, scroll locking and ARIA —
 * the parts that are easy to get subtly wrong and expensive to discover
 * wrong. Aeris supplies only the styling.
 *
 * Animations stay short and opacity/transform based: no blur, no backdrop
 * filters (docs/UI_UX.md, and a real cost on Celeron-class hardware).
 */
export const TooltipProvider = TooltipPrimitive.Provider;
export const Separator = SeparatorPrimitive.Root;
export const Tooltip = ({ content, children, side = 'right', disabled = false, delay, }) => {
    if (disabled)
        return _jsx(_Fragment, { children: children });
    return (_jsxs(TooltipPrimitive.Root, { ...(delay === undefined ? {} : { delayDuration: delay }), children: [_jsx(TooltipPrimitive.Trigger, { asChild: true, children: children }), _jsx(TooltipPrimitive.Portal, { children: _jsx(TooltipPrimitive.Content, { side: side, sideOffset: 6, className: cn('z-(--z-tooltip) rounded-(--radius-md) px-2 py-1', 'bg-(--aeris-slate-900) text-white text-(length:--text-xs)', 'shadow-(--shadow-md) select-none', 
                    // `instant-open` as well as `delayed-open`: with delay={0} Radix
                    // reports the former, and a tooltip with no animation class would
                    // simply appear.
                    'data-[state=delayed-open]:animate-in data-[state=instant-open]:animate-in data-[state=closed]:animate-out', 'data-[state=delayed-open]:fade-in-0 data-[state=instant-open]:fade-in-0 data-[state=closed]:fade-out-0'), children: content }) })] }));
};
/**
 * O diálogo — e, dentro de um lugar que contém diálogos, contido nele (F138).
 *
 * Sem host é o `Dialog` do Radix de sempre, modal para a página. Dentro de uma
 * janela do Modo PC não pode ser: o modo modal do Radix desliga o ponteiro do
 * `body` inteiro e esconde tudo do leitor de tela, o que travaria as outras
 * janelas junto. Lá ele é modal só para a janela dona, que quem desenha a janela
 * bloqueia (`OverlayHost.hold`).
 */
export const Dialog = ({ modal, ...props }) => {
    const host = useOverlayHost();
    const contained = host?.layer !== undefined && host.layer !== null;
    return (_jsx(DialogPrimitive.Root, { ...props, modal: contained ? false : (modal ?? true) }));
};
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
/** A linha do `meta`, igual nas duas molduras do diálogo. */
const DialogMeta = ({ children, onPointerDown, }) => (_jsx("div", { 
    // Dá para selecionar o código, mesmo na barra que arrasta a janela.
    className: "flex cursor-auto flex-wrap items-center gap-x-4 gap-y-1 pt-1 select-text", ...(onPointerDown === undefined ? {} : { onPointerDown }), children: children }));
/** O diálogo que cobre a página — o de sempre, fora do Modo PC. */
const PageDialogContent = forwardRef(({ className, title, description, meta, children, ...props }, ref) => (_jsxs(DialogPrimitive.Portal, { children: [_jsx(DialogPrimitive.Overlay, { className: cn('fixed inset-0 z-(--z-overlay) bg-(--surface-overlay)', 'data-[state=open]:animate-in data-[state=open]:fade-in-0', 'data-[state=closed]:animate-out data-[state=closed]:fade-out-0') }), _jsxs(DialogPrimitive.Content, { ref: ref, className: cn('fixed left-1/2 top-1/2 z-(--z-dialog) w-full max-w-lg -translate-x-1/2 -translate-y-1/2', 'rounded-(--radius-lg) border border-(--border-default) bg-(--surface-raised)', 'shadow-(--shadow-lg)', 
            // A dialog never grows past the window. `dvh` rather than `vh` because
            // a phone's address bar makes the two differ by exactly enough to hide
            // a Salvar button. The gutter keeps it from touching the edges.
            'flex max-h-[calc(100dvh-2rem)] flex-col', 'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95', 'data-[state=closed]:animate-out data-[state=closed]:fade-out-0', className), ...props, children: [_jsxs("div", { className: "flex shrink-0 items-start justify-between gap-4 px-5 pt-5 pb-4", children: [_jsxs("div", { className: "flex min-w-0 flex-col gap-1", children: [_jsx(DialogPrimitive.Title, { className: "text-(length:--text-lg) font-(--weight-semibold) text-(--text-primary)", children: title }), description ? (_jsx(DialogPrimitive.Description, { className: "text-(length:--text-sm) text-(--text-secondary)", children: description })) : null, meta === undefined || meta === null ? null : (_jsx(DialogMeta, { children: meta }))] }), _jsx(DialogPrimitive.Close, { "aria-label": "Fechar", className: cn('rounded-(--radius-sm) p-1 text-(--text-muted)', 'transition-colors hover:bg-(--surface-hover) hover:text-(--text-primary)'), children: _jsx(X, { "aria-hidden": "true", className: "h-4 w-4" }) })] }), _jsx("div", { className: "min-h-0 flex-1 overflow-y-auto px-5 pb-5", children: children })] })] })));
PageDialogContent.displayName = 'PageDialogContent';
/**
 * Os Esc que um diálogo contido marcou só para o Radix não fechar sozinho.
 *
 * O Radix escuta o Esc no documento, antes do React, e só deixa de fechar se o
 * evento vier marcado (`preventDefault`). Essa marca é a mesma que um campo usa
 * para dizer "este Esc é meu" — então quem marcou por causa do Radix fica
 * anotado aqui, e as duas marcas não se confundem.
 */
const containedEscapes = new WeakSet();
/**
 * O que segura o dono, e só enquanto o diálogo está aberto.
 *
 * Filho do `Content`, que o Radix só monta aberto. Muitas telas deixam o
 * `DialogContent` montado o tempo todo e só trocam o `open`: um bloqueio preso
 * à montagem dele travaria a janela com a ficha fechada.
 */
const HoldOwner = ({ host, onRelease, }) => {
    useEffect(() => {
        const release = host.hold?.();
        return () => {
            release?.();
            onRelease();
        };
    }, [host, onRelease]);
    return null;
};
/**
 * O diálogo como janela filha — F138.
 *
 * Desenhado na camada da janela dona, e não no corpo da página. O que muda em
 * relação ao de sempre, e por quê:
 *
 * - **Clicar fora não fecha.** O Radix fecha um diálogo não modal ao primeiro
 *   clique ou foco fora dele; aqui "fora" é outra janela, e a ficha de um
 *   produto não pode sumir porque alguém clicou em Clientes.
 * - **O Esc é tratado aqui, e não pelo Radix.** O Esc do Radix vale para o
 *   documento inteiro e fecha a camada mais alta DA PÁGINA: apertado na janela
 *   de Clientes, fecharia a ficha aberta na de Produtos. Aqui ele só fecha
 *   quando nasce dentro deste diálogo, e só se nenhum outro por cima dele — uma
 *   confirmação — estiver aberto.
 * - **Bloqueia o dono**, e só ele (`host.hold`): a tela de trás tem um estado
 *   só de "o que está sendo editado", e mexer nela com a ficha aberta trocaria
 *   o registro debaixo do formulário.
 * - **Um diálogo aberto dentro deste** — a confirmação de excluir — abre na
 *   mesma camada, por cima, e bloqueia este.
 * - **Arrasta pelo título**, como uma janela.
 */
const ContainedDialogContent = forwardRef(({ className, title, description, meta, children, host, layer, onKeyDown, onPointerDownOutside, onInteractOutside, onFocusOutside, onEscapeKeyDown, style, ...props }, ref) => {
    const boxRef = useRef(null);
    const closeRef = useRef(null);
    const [nested, setNested] = useState(0);
    const nestedRef = useRef(0);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    useEffect(() => {
        nestedRef.current = nested;
    });
    // Fechado, o diálogo volta a abrir no centro, e não onde foi arrastado.
    const resetOffset = useCallback(() => {
        setOffset({ x: 0, y: 0 });
    }, []);
    /** O lugar para quem abrir um diálogo dentro deste. */
    const nestedHost = useMemo(() => ({
        layer,
        isActive: () => host.isActive?.() ?? true,
        hold: () => {
            setNested((count) => count + 1);
            return () => {
                setNested((count) => count - 1);
            };
        },
        ownsKeyboard: (event) => (host.isActive?.() ?? true) &&
            nestedRef.current === 0 &&
            ownsWithin(boxRef.current, event.target),
    }), [host, layer]);
    const setBox = (node) => {
        boxRef.current = node;
        if (typeof ref === 'function')
            ref(node);
        else if (ref !== null)
            ref.current = node;
    };
    const translate = (x, y) => `translate(calc(-50% + ${String(x)}px), calc(-50% + ${String(y)}px))`;
    const onTitlePointerDown = (event) => {
        const box = boxRef.current;
        if (box === null)
            return;
        const start = offset;
        startPointerDrag(event, {
            onMove: ({ dx, dy }) => {
                box.style.transform = translate(start.x + dx, start.y + dy);
            },
            onEnd: ({ dx, dy }) => {
                setOffset({ x: start.x + dx, y: start.y + dy });
            },
            onCancel: () => {
                box.style.transform = translate(start.x, start.y);
            },
        });
    };
    const onContentKeyDown = (event) => {
        onKeyDown?.(event);
        if (event.key !== 'Escape')
            return;
        // Marcado por quem não é um diálogo contido: um campo já tratou o Esc.
        if (event.defaultPrevented && !containedEscapes.has(event.nativeEvent)) {
            return;
        }
        // Uma confirmação aberta por cima deste é quem responde.
        if (nestedRef.current > 0)
            return;
        /*
         * Só o Esc que nasceu aqui dentro. A árvore do React atravessa portais:
         * o Esc de um menu aberto no corpo da página também subiria até aqui.
         */
        const box = boxRef.current;
        const target = event.target;
        if (box === null || !(target instanceof Node) || !box.contains(target)) {
            return;
        }
        // Uma lista aberta num campo fecha a lista, e não a ficha inteira.
        const expanded = target instanceof Element
            ? target.closest('[aria-expanded="true"]')
            : null;
        if (expanded !== null && box.contains(expanded))
            return;
        event.stopPropagation();
        closeRef.current?.click();
    };
    return (_jsx(DialogPrimitive.Portal, { container: layer, children: _jsxs(DialogPrimitive.Content, { ref: setBox, "data-desktop-window": "", "data-contained-dialog": "", ...props, onPointerDownOutside: (event) => {
                onPointerDownOutside?.(event);
                event.preventDefault();
            }, onInteractOutside: (event) => {
                onInteractOutside?.(event);
                event.preventDefault();
            }, onFocusOutside: (event) => {
                onFocusOutside?.(event);
                event.preventDefault();
            }, onEscapeKeyDown: (event) => {
                onEscapeKeyDown?.(event);
                containedEscapes.add(event);
                event.preventDefault();
            }, onKeyDown: onContentKeyDown, className: cn('pointer-events-auto absolute left-1/2 top-1/2 w-[calc(100%-2rem)] max-w-lg', 'flex max-h-[calc(100%-2rem)] flex-col', 'rounded-(--radius-lg) border border-(--window-border-active) bg-(--surface-raised)', 'shadow-(--shadow-lg)', 
            // Só opacidade: a posição é um `transform`, e uma animação de
            // escala por cima faria o diálogo pular de lugar ao abrir.
            'data-[state=open]:animate-in data-[state=open]:fade-in-0', className), style: { ...style, transform: translate(offset.x, offset.y) }, children: [_jsx(HoldOwner, { host: host, onRelease: resetOffset }), _jsxs("div", { inert: nested > 0, className: "flex min-h-0 flex-1 flex-col", children: [_jsxs("div", { onPointerDown: onTitlePointerDown, className: "flex shrink-0 cursor-move select-none items-start justify-between gap-4 px-5 pt-5 pb-4", children: [_jsxs("div", { className: "flex min-w-0 flex-col gap-1", children: [_jsx(DialogPrimitive.Title, { className: "text-(length:--text-lg) font-(--weight-semibold) text-(--text-primary)", children: title }), description ? (_jsx(DialogPrimitive.Description, { className: "text-(length:--text-sm) text-(--text-secondary)", children: description })) : null, meta === undefined || meta === null ? null : (_jsx(DialogMeta
                                        // A barra arrasta a janela; o código não — copiar e
                                        // selecionar não podem virar arrastar.
                                        , { 
                                            // A barra arrasta a janela; o código não — copiar e
                                            // selecionar não podem virar arrastar.
                                            onPointerDown: (event) => {
                                                event.stopPropagation();
                                            }, children: meta }))] }), _jsx(DialogPrimitive.Close, { ref: closeRef, "aria-label": "Fechar", 
                                    // O botão mora na barra que arrasta.
                                    onPointerDown: (event) => {
                                        event.stopPropagation();
                                    }, className: cn('rounded-(--radius-sm) p-1 text-(--text-muted)', 'transition-colors hover:bg-(--surface-hover) hover:text-(--text-primary)'), children: _jsx(X, { "aria-hidden": "true", className: "h-4 w-4" }) })] }), _jsx(OverlayHostContext, { value: nestedHost, children: _jsx("div", { className: "min-h-0 flex-1 overflow-y-auto px-5 pb-5", children: children }) })] }), nested > 0 ? (_jsx("div", { "aria-hidden": "true", className: "absolute inset-0 rounded-(--radius-lg) bg-(--surface-overlay)" })) : null] }) }));
});
ContainedDialogContent.displayName = 'ContainedDialogContent';
export const DialogContent = forwardRef(({ onOpenAutoFocus, onCloseAutoFocus, ...rest }, ref) => {
    const host = useOverlayHost();
    /*
     * O foco volta para quem abriu — F248.
     *
     * Sem `DialogTrigger` (o app controla o `open` em 85 diálogos e não usa
     * nenhum), o Radix devolvia o foco a um gatilho que não existe, e ele caía
     * no `<body>`: quem fechava a ficha pelo teclado recomeçava o Tab do topo
     * da página. Aqui o diálogo lembra o que tinha o foco ao abrir e devolve.
     */
    const opener = useRef(null);
    const props = {
        ...rest,
        onOpenAutoFocus: (event) => {
            const active = document.activeElement;
            opener.current =
                active instanceof HTMLElement && active !== document.body
                    ? active
                    : null;
            onOpenAutoFocus?.(event);
        },
        onCloseAutoFocus: (event) => {
            onCloseAutoFocus?.(event);
            if (event.defaultPrevented)
                return;
            const target = opener.current;
            opener.current = null;
            if (target?.isConnected === true) {
                event.preventDefault();
                target.focus({ preventScroll: true });
            }
        },
    };
    if (host?.layer !== undefined && host.layer !== null) {
        return (_jsx(ContainedDialogContent, { ...props, host: host, layer: host.layer, ref: ref }));
    }
    return _jsx(PageDialogContent, { ...props, ref: ref });
});
DialogContent.displayName = 'DialogContent';
/**
 * The action row of a dialog: Cancelar, Salvar.
 *
 * Sticky rather than fixed, and deliberately rendered *inside* the scrolling
 * body rather than as a sibling of it. That placement is the whole point: the
 * buttons stay inside the consumer's `<form>`, so `type="submit"` keeps
 * working and Enter still submits. A footer hoisted out of the scroll region
 * would need every dialog to grow an id and a `form=` attribute — nineteen
 * chances to wire one wrong, in exchange for nothing the user can see.
 *
 * The border only appears when there is something scrolled underneath it,
 * which is what `bg` + `border-t` buy: on a short dialog the footer sits at
 * the natural end of the content and the line reads as a divider; on a long
 * one it reads as the edge of the scroll.
 *
 * `-bottom-5`, not `bottom-0` (F136). A sticky box is held inside the scroll
 * container's box MINUS its padding, and the body has `pb-5`. With
 * `bottom-0` the footer — which `-mb-5` already pulls down into that padding —
 * was pushed back up 20px on EVERY dialog, eating the `mt-4` gap and 4px of
 * the last field: the "Nova troca" search box showed with its bottom edge and
 * focus ring cut. Offsetting by the same 20px lands the sticky edge exactly on
 * the visible bottom. Measured in real Chromium, short and long dialogs.
 */
export const DialogFooter = forwardRef(({ className, ...props }, ref) => (_jsx("div", { ref: ref, className: cn('sticky -bottom-5 z-10 -mx-5 -mb-5 mt-4 flex flex-wrap justify-end gap-2', 'border-t border-(--border-subtle) bg-(--surface-raised) px-5 py-3', className), ...props })));
DialogFooter.displayName = 'DialogFooter';
export const DropdownMenu = DropdownMenuPrimitive.Root;
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
export const DropdownMenuSeparator = forwardRef(({ className, ...props }, ref) => (_jsx(DropdownMenuPrimitive.Separator, { ref: ref, className: cn('my-1 h-px bg-(--border-subtle)', className), ...props })));
DropdownMenuSeparator.displayName = 'DropdownMenuSeparator';
export const DropdownMenuContent = forwardRef(({ className, sideOffset = 6, ...props }, ref) => (_jsx(DropdownMenuPrimitive.Portal, { children: _jsx(DropdownMenuPrimitive.Content, { ref: ref, sideOffset: sideOffset, className: cn('z-(--z-dropdown) min-w-44 overflow-hidden rounded-(--radius-md) p-1', 'border border-(--border-default) bg-(--surface-raised) shadow-(--shadow-md)', 'data-[state=open]:animate-in data-[state=open]:fade-in-0', 'data-[state=closed]:animate-out data-[state=closed]:fade-out-0', className), ...props }) })));
DropdownMenuContent.displayName = 'DropdownMenuContent';
export const DropdownMenuItem = forwardRef(({ className, ...props }, ref) => (_jsx(DropdownMenuPrimitive.Item, { ref: ref, className: cn('flex cursor-pointer select-none items-center gap-2 rounded-(--radius-sm) px-2 py-1.5', 'text-(length:--text-sm) text-(--text-primary) outline-none', 'data-[highlighted]:bg-(--surface-hover)', 'data-[disabled]:pointer-events-none data-[disabled]:opacity-50', className), ...props })));
DropdownMenuItem.displayName = 'DropdownMenuItem';
export const DropdownMenuCheckboxItem = forwardRef(({ className, children, ...props }, ref) => (_jsxs(DropdownMenuPrimitive.CheckboxItem, { ref: ref, className: cn('relative flex cursor-pointer select-none items-center gap-2 rounded-(--radius-sm) py-1.5 pr-2 pl-7', 'text-(length:--text-sm) text-(--text-primary) outline-none', 'data-[highlighted]:bg-(--surface-hover)', 'data-[disabled]:pointer-events-none data-[disabled]:opacity-50', className), ...props, children: [_jsx("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: _jsx(DropdownMenuPrimitive.ItemIndicator, { children: _jsx(Check, { "aria-hidden": "true", className: "h-3.5 w-3.5" }) }) }), children] })));
DropdownMenuCheckboxItem.displayName = 'DropdownMenuCheckboxItem';
export const DropdownMenuLabel = forwardRef(({ className, ...props }, ref) => (_jsx(DropdownMenuPrimitive.Label, { ref: ref, className: cn('px-2 py-1.5 text-(length:--text-xs) font-(--weight-semibold) text-(--text-muted)', className), ...props })));
DropdownMenuLabel.displayName = 'DropdownMenuLabel';
//# sourceMappingURL=overlay.js.map